

// const BASE_URL = "http://192.168.232.220:9001";

import { useState, useEffect, useRef } from 'react';
import './Qhse.css';
import './homepageEditor.css'; // adjust path as needed
import Avatar from '../../Images/image14.png';

const API_BASE      = "http://localhost:5000/api/qhse";
const UPLOAD_URL    = "http://localhost:5000/api/upload";
const SERVER_ORIGIN = "http://localhost:5000";

const resolveUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  return `${SERVER_ORIGIN}${url}`;
};

const DEFAULT_DATA = {
  heroImage:   "",
  intro:       "We recognise that everyone's interest is best served when health and safety becomes an integral part of our operations, not necessarily because it is required but because we want to. It is therefore no surprise that we have excellent safety records throughout our entire operation.",
  quote:       '"Be in Quality while you put safety first"',
  subIntro:    "To live this slogan, we operate and work within the guidelines of standards that demand of a responsible relationship with our clients, employees and communities in which we operate.",
  policyIntro: "Our QHSE Policy encompasses Quality, Health, Safety and Environmental Management based on the following principles:",
  cards: [
    {
      title: "Quality",
      paragraphs: [
        '"Be in Quality while you put safety first".',
        "To live this slogan, we operate and work within standard guidelines that demand of us a responsible relationship with our clients, employees, and host communities.",
        "BOG received its Integrated Management System (IMS) Certification from the Standards Organization of Nigeria (SON) in 2020. This encompasses ISO 9001:2015 Quality Management System, ISO 45001:2018 Occupational Health & Safety, and ISO 14001:2015 Environmental Management System.",
      ],
    },
    {
      title: "Health & Safety Management",
      paragraphs: [
        "We are concerned about the improvement of health and safety conditions in our working environment and we take into cognizance the specifics of our processes. This is an integral part of our business and reflects our commitment to the care for our employees and attitude towards the social environment where we live and operate.",
        "For us at Bell Oil & Gas, compliance with the relevant legislations represents merely the minimum level to be achieved. Our aim is to surpass this standard, using our HSE management system as a framework for continual improvement.",
      ],
    },
  ],
};

/* =========================================================
   IMAGE UPLOAD FIELD
========================================================= */
function ImageUploadField({ label, currentUrl, onUploaded }) {
  const [preview,   setPreview]   = useState(resolveUrl(currentUrl) || null);
  const [uploading, setUploading] = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef();

  useEffect(() => { setPreview(resolveUrl(currentUrl) || null); }, [currentUrl]);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setError(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("image", file);
      const res  = await fetch(UPLOAD_URL, { method: "POST", body: formData });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Upload failed");
      onUploaded(json.url);
      setPreview(resolveUrl(json.url));
    } catch (err) {
      setError("Upload failed. Try again.");
    } finally { setUploading(false); }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploaded("");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="editor-img-field">
      <label>{label}</label>
      {preview ? (
        <div className="editor-img-preview-wrap">
          <img src={preview} alt="preview" className="editor-img-preview" />
          {uploading && <div className="editor-img-uploading"><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></div>}
          {!uploading && (
            <div className="editor-img-actions">
              <button type="button" className="editor-img-btn editor-img-btn--change" onClick={() => inputRef.current?.click()}>Change</button>
              <button type="button" className="editor-img-btn editor-img-btn--remove" onClick={handleRemove}>Remove</button>
            </div>
          )}
        </div>
      ) : (
        <button type="button" className="editor-img-dropzone" onClick={() => inputRef.current?.click()} disabled={uploading}>
          {uploading
            ? <><div className="editor-loading__spinner editor-loading__spinner--sm" /><span>Uploading...</span></>
            : <><span className="editor-img-dropzone__icon">🖼️</span><span>Click to upload image</span><span className="editor-img-dropzone__hint">JPG, PNG, WEBP — max 10MB</span></>
          }
        </button>
      )}
      {error && <p className="editor-img-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={handleFile} />
    </div>
  );
}

/* =========================================================
   EDIT MODAL
========================================================= */
function QhseEditModal({ data, onSave, onClose, saving }) {
  const [draft, setDraft] = useState(() => JSON.parse(JSON.stringify(data)));

  /* Card helpers */
  const setCardField = (ci, field, val) =>
    setDraft(d => { const cards = [...d.cards]; cards[ci] = { ...cards[ci], [field]: val }; return { ...d, cards }; });

  const setCardPara = (ci, pi, val) =>
    setDraft(d => {
      const cards = [...d.cards];
      const paragraphs = [...cards[ci].paragraphs];
      paragraphs[pi] = val;
      cards[ci] = { ...cards[ci], paragraphs };
      return { ...d, cards };
    });

  const addCardPara = (ci) =>
    setDraft(d => {
      const cards = [...d.cards];
      cards[ci] = { ...cards[ci], paragraphs: [...cards[ci].paragraphs, "New paragraph..."] };
      return { ...d, cards };
    });

  const removeCardPara = (ci, pi) =>
    setDraft(d => {
      const cards = [...d.cards];
      cards[ci] = { ...cards[ci], paragraphs: cards[ci].paragraphs.filter((_, i) => i !== pi) };
      return { ...d, cards };
    });

  const addCard = () =>
    setDraft(d => ({ ...d, cards: [...d.cards, { title: "New Card", paragraphs: ["New paragraph..."] }] }));

  const removeCard = (ci) =>
    setDraft(d => ({ ...d, cards: d.cards.filter((_, i) => i !== ci) }));

  return (
    <div className="editor-overlay" onClick={onClose}>
      <div className="editor-modal" onClick={e => e.stopPropagation()}>

        <div className="editor-modal__header">
          <h2>Edit QHSE Page</h2>
          <button className="editor-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="editor-modal__body">

          {/* HERO IMAGE */}
          <p className="editor-section__title">Hero Image</p>
          <div className="editor-group">
            <ImageUploadField
              label="Page Banner Image"
              currentUrl={draft.heroImage}
              onUploaded={url => setDraft(d => ({ ...d, heroImage: url }))}
            />
          </div>

          {/* PAGE TEXT */}
          <p className="editor-section__title">Page Text</p>
          <div className="editor-group">
            <label>Intro Paragraph</label>
            <textarea
              rows={4}
              value={draft.intro}
              onChange={e => setDraft(d => ({ ...d, intro: e.target.value }))}
            />
            <label>Quote</label>
            <input
              value={draft.quote}
              onChange={e => setDraft(d => ({ ...d, quote: e.target.value }))}
            />
            <label>Sub-Intro Paragraph</label>
            <textarea
              rows={3}
              value={draft.subIntro}
              onChange={e => setDraft(d => ({ ...d, subIntro: e.target.value }))}
            />
            <label>Policy Intro Paragraph</label>
            <textarea
              rows={3}
              value={draft.policyIntro}
              onChange={e => setDraft(d => ({ ...d, policyIntro: e.target.value }))}
            />
          </div>

          {/* CARDS */}
          <p className="editor-section__title">QHSE Cards</p>
          {draft.cards.map((card, ci) => (
            <div key={ci} className="editor-group">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <p className="editor-group__label">{card.title || `Card ${ci + 1}`}</p>
                {draft.cards.length > 1 && (
                  <button className="editor-value-row__remove" onClick={() => removeCard(ci)} title="Remove card">✕</button>
                )}
              </div>

              <label>Card Title</label>
              <input
                value={card.title}
                onChange={e => setCardField(ci, "title", e.target.value)}
              />

              <label style={{ marginTop: "8px" }}>Paragraphs</label>
              {card.paragraphs.map((p, pi) => (
                <div key={pi} className="editor-value-row" style={{ alignItems: "flex-start" }}>
                  <textarea
                    rows={3}
                    value={p}
                    onChange={e => setCardPara(ci, pi, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <button className="editor-value-row__remove" onClick={() => removeCardPara(ci, pi)} title="Remove">✕</button>
                </div>
              ))}
              <button className="editor-add-btn" onClick={() => addCardPara(ci)}>+ Add Paragraph</button>
            </div>
          ))}
          <button className="editor-add-btn" onClick={addCard}>+ Add Card</button>

        </div>

        <div className="editor-modal__footer">
          <button className="btn btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn--orange" onClick={() => onSave(draft)} disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>

      </div>
    </div>
  );
}

/* =========================================================
   MAIN EXPORT
========================================================= */
const AdminSafetyPolicy = () => {
  const [pageData,  setPageData]  = useState(DEFAULT_DATA);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res  = await fetch(API_BASE);
      const json = await res.json();
      if (json.success) setPageData(json.data);
    } catch (err) { console.error("Fetch error:", err); }
  };

  const handleSave = async (draft) => {
    try {
      setSaving(true);
      const res  = await fetch(API_BASE, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(draft) });
      const json = await res.json();
      if (!json.success) throw new Error(json.message);
      await fetchData();
      setModalOpen(false);
      setSaveMsg("Changes saved successfully!");
    } catch (err) {
      setSaveMsg("Save failed. Please try again.");
    } finally {
      setSaving(false);
      setTimeout(() => setSaveMsg(null), 3500);
    }
  };

  const heroBg = resolveUrl(pageData.heroImage) || Avatar;

  return (
    <>
      <button className="edit-fab" onClick={() => setModalOpen(true)} title="Edit page content">
        <span className="edit-fab__icon">✏️</span>
        <span className="edit-fab__label">Edit Page</span>
      </button>

      {saveMsg && <div className="editor-toast">{saveMsg}</div>}

      {modalOpen && (
        <QhseEditModal
          data={pageData}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
          saving={saving}
        />
      )}

      <div className="img_carrier">
        <img src={heroBg} alt="" />
      </div>

      <section className="qhse-section" aria-labelledby="qhse-title">
        <div className="qhse-section__header">
          <h2 className="qhse-section__title" id="qhse-title">QHSE</h2>
          <hr className="qhse-section__divider" />
        </div>

        <p className="qhse-section__intro">{pageData.intro}</p>

        <div className="qhse-section__quote-box">
          <p className="qhse-section__quote">{pageData.quote}</p>
        </div>

        <p className="qhse-section__sub-intro">{pageData.subIntro}</p>

        <p className="qhse-section__policy-intro">{pageData.policyIntro}</p>

        <div className="qhse-section__cards">
          {pageData.cards.map((card, i) => (
            <div className="qhse-card" key={i}>
              <h3 className="qhse-card__title">{card.title}</h3>
              {card.paragraphs.map((para, j) => (
                <p className="qhse-card__text" key={j}>{para}</p>
              ))}
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

export default AdminSafetyPolicy;